import { useState } from "react";
import { createRoute, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { rootRoute } from "./root.tsx";
import { SubPage } from "../components/layout.tsx";
import { Empty, ErrorBlock, Price, Spinner } from "../components/ui.tsx";
import { useIdentities, useMe } from "../api/queries.ts";
import { orgFetch } from "../api/org-client.ts";
import { useIsDesktop } from "../lib/viewport.ts";
import { DesktopEarnings } from "./desktop/earnings.tsx";

/**
 * 收益明细。
 *
 * 🔴 之前商家只看得到三个汇总数字（可提现 / 待结算 / 提现中），
 *    **每一笔钱从哪来、为什么被扣，一概看不到** ——
 *    券花了多少、被上级抽了多少、退款扣回了多少，全是黑箱。
 *    商家看到余额比预期少时只能来问，客服也答不上来。
 */

export interface EarningItem {
  id: number;
  orderId: number;
  type: string;
  amount: number;
  baseAmount: number;
  rate: string | null;
  status: string;
  note: string;
  settleableAt: string | null;
  createdAt: string;
}

export interface EarningsResp {
  summary: { pending: number; settleable: number; settled: number; reversed: number };
  items: EarningItem[];
  nextCursor: number | null;
}

/**
 * 账户流水 —— 跟分账明细**不是一回事**：
 * 分账说的是"这一单我该分多少"，流水说的是"我账上的钱实际怎么进出的"。
 * 结算入账、提现扣款、提现驳回退回都只在流水里，商家对账要看这个。
 */
export interface TxnRow {
  id: number;
  type: string;
  amount: number;
  balanceAfter: number;
  note: string;
  createdAt: string;
}

export const TXN_LABEL: Record<string, string> = {
  settlement_in: "结算入账",
  withdrawal_out: "提现扣款",
  withdrawal_refund: "提现退回",
  fee_out: "费用扣除",
  refund_offset: "退款扣回",
  deposit_in: "保证金存入",
  deposit_out: "保证金退还",
  manual_adjust: "人工调整",
};

/** 分账类型的中文说法。⚠️ 认不出来的原样显示，别默默吞掉。 */
export const TYPE_LABEL: Record<string, string> = {
  margin: "差价",
  freight: "运费",
  commission_gmv: "按成交额抽成",
  commission_profit: "按利润抽成",
  per_order: "按单收费",
  periodic_fee: "周期费",
  deposit: "保证金",
  refund_offset: "退款扣回",
  coupon_discount: "优惠券支出",
  manual_adjust: "人工调整",
};

export const STATUS_LABEL: Record<string, string> = {
  pending: "待结算",
  settleable: "可结算",
  settled: "已入账",
  reversed: "已冲抵",
};

export interface FeeSubRow {
  id: number;
  period: string;
  amount: number;
  status: string;
  currentPeriodEnd: string;
  nextBillAt: string | null;
}
export interface FeeDepositRow {
  id: number;
  amount: number;
  status: string;
  paidAt: string | null;
  forfeitReason: string;
}

const orgGet = <T,>(path: string, orgId: number) => orgFetch<T>(path, orgId);

function Earnings() {
  const search = useSearch({ from: "/earnings" }) as { orgId?: number };
  const { data: me, isPending: mePending } = useMe();
  const { data: identities } = useIdentities(Boolean(me));

  const orgs = (identities?.orgs ?? []).filter((o) => o.status === "active");
  const [picked, setPicked] = useState<number | null>(search.orgId ?? null);
  const orgId = picked ?? orgs[0]?.id ?? null;

  const [tab, setTab] = useState<"items" | "txns" | "fees">("items");

  const data = useQuery({
    queryKey: ["earnings-detail", orgId],
    queryFn: () => orgGet<EarningsResp>("/earnings?limit=100", orgId!),
    enabled: Boolean(orgId) && tab === "items",
  });

  const txns = useQuery({
    queryKey: ["txns", orgId],
    queryFn: () => orgGet<{ items: TxnRow[] }>("/txns?limit=100", orgId!),
    enabled: Boolean(orgId) && tab === "txns",
  });

  /**
   * 🔴 **押金要单独摆出来。** 它交的时候就从余额扣走了，
   *    既不在可提现余额里、也不在待结算里 —— 不列出来的话
   *    商家只会看到"钱少了一笔"，来问客服客服也答不上来。
   */
  const fees = useQuery({
    queryKey: ["fee-account", orgId],
    queryFn: () =>
      orgGet<{ subscriptions: FeeSubRow[]; deposits: FeeDepositRow[]; held: number }>(
        "/fee-account",
        orgId!,
      ),
    enabled: Boolean(orgId) && tab === "fees",
  });

  if (mePending) {
    return (
      <SubPage title="收益明细">
        <Spinner />
      </SubPage>
    );
  }
  if (!me || orgs.length === 0) {
    return (
      <SubPage title="收益明细">
        <Empty title="你还不是任何商家的成员" />
      </SubPage>
    );
  }

  return (
    <SubPage title="收益明细">
      {orgs.length > 1 && (
        <div className="flex gap-2 overflow-x-auto bg-white px-4 py-2.5">
          {orgs.map((o) => (
            <button
              key={o.id}
              onClick={() => setPicked(o.id)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-sm ${
                o.id === orgId ? "bg-brand-600 text-white" : "bg-neutral-100 text-ink-500"
              }`}
            >
              {o.name}
            </button>
          ))}
        </div>
      )}

      <div className="flex gap-2 bg-white px-4 py-2.5">
        {(
          [
            ["items", "分账明细"],
            ["txns", "资金流水"],
            ["fees", "费用与押金"],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`rounded-full px-3 py-1.5 text-sm ${
              tab === k ? "bg-brand-600 text-white" : "bg-neutral-100 text-ink-500"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "fees" ? (
        fees.isPending ? (
          <Spinner />
        ) : fees.error ? (
          <ErrorBlock error={fees.error} onRetry={() => fees.refetch()} />
        ) : (
          <>
            <div className="mt-2 bg-white px-4 py-3">
              <p className="text-xs text-ink-500">押在平台的保证金</p>
              <p className="mt-1 text-lg">
                <Price cents={fees.data!.held} />
              </p>
              {/* 🔴 说清楚这笔钱去哪了，否则商家以为系统把钱吞了 */}
              <p className="mt-1 text-xs text-ink-500">
                这笔钱交的时候就从余额扣走了，<strong>不在可提现余额里</strong>；
                退出渠道时会退回你的余额。
              </p>
            </div>

            <p className="px-4 pb-1 pt-3 text-xs text-ink-500">周期费</p>
            {fees.data!.subscriptions.length === 0 ? (
              <Empty title="没有周期费" hint="平台没有向你收年费或月费" />
            ) : (
              <div className="bg-white">
                {fees.data!.subscriptions.map((s) => (
                  <div key={s.id} className="border-b border-neutral-100 px-4 py-3 last:border-0">
                    <p className="text-sm">
                      {s.period === "monthly" ? "月费" : s.period === "quarterly" ? "季费" : "年费"}
                      <span className="ml-1.5 rounded bg-neutral-100 px-1.5 py-0.5 text-[11px] text-ink-500">
                        {s.status === "active"
                          ? "生效中"
                          : s.status === "expired"
                            ? "已过期"
                            : "已取消"}
                      </span>
                    </p>
                    <p className="mt-0.5 text-xs text-ink-500">
                      每期 <Price cents={s.amount} />
                      {s.nextBillAt && (
                        <span className="ml-1.5">
                          · {new Date(s.nextBillAt).toLocaleDateString("zh-CN")} 扣下一期
                        </span>
                      )}
                    </p>
                    {/* ⚠️ 过期不等于停售 —— 这句话必须说，否则商家会慌 */}
                    {s.status === "expired" && (
                      <p className="mt-1 text-xs text-amber-700">
                        上次扣款时余额不足，已暂停续期。<strong>不影响你正常卖货</strong>。
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            <p className="px-4 pb-1 pt-3 text-xs text-ink-500">保证金记录</p>
            {fees.data!.deposits.length === 0 ? (
              <Empty title="没有保证金记录" />
            ) : (
              <div className="bg-white">
                {fees.data!.deposits.map((d) => (
                  <div
                    key={d.id}
                    className="flex items-start gap-3 border-b border-neutral-100 px-4 py-3 last:border-0"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm">
                        {d.status === "held"
                          ? "在押"
                          : d.status === "refunded"
                            ? "已退还"
                            : d.status === "forfeited"
                              ? "已没收"
                              : d.status}
                      </p>
                      {d.forfeitReason && (
                        <p className="mt-0.5 text-xs text-red-600">{d.forfeitReason}</p>
                      )}
                      <p className="mt-0.5 text-xs text-ink-500">
                        {d.paidAt ? new Date(d.paidAt).toLocaleDateString("zh-CN") : ""}
                      </p>
                    </div>
                    <Price cents={d.amount} />
                  </div>
                ))}
              </div>
            )}
          </>
        )
      ) : tab === "txns" ? (
        txns.isPending ? (
          <Spinner />
        ) : txns.error ? (
          <ErrorBlock error={txns.error} onRetry={() => txns.refetch()} />
        ) : (txns.data?.items.length ?? 0) === 0 ? (
          <Empty title="还没有资金流水" hint="结算入账、提现都会记在这里" />
        ) : (
          <>
            {/*
              🔴 这一句是对账的关键：每笔流水都带**变动后的余额**，
                 所以"钱去哪了"永远查得到，不用靠猜。
            */}
            <p className="px-4 py-2 text-xs text-ink-500">
              每一笔都记着<strong>变动后的余额</strong>，从上往下能对上账。
            </p>
            <div className="bg-white">
              {txns.data!.items.map((t) => (
                <div
                  key={t.id}
                  className="flex items-start gap-3 border-b border-neutral-100 px-4 py-3 last:border-0"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm">{TXN_LABEL[t.type] ?? t.type}</p>
                    {t.note && <p className="mt-0.5 text-xs text-ink-500">{t.note}</p>}
                    <p className="mt-0.5 text-xs text-ink-500">
                      {new Date(t.createdAt).toLocaleString("zh-CN")}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className={`text-sm ${t.amount < 0 ? "text-red-600" : "text-brand-600"}`}>
                      {t.amount < 0 ? "−" : "+"}
                      <Price cents={Math.abs(t.amount)} />
                    </p>
                    <p className="mt-0.5 text-xs text-ink-500">
                      余额 <Price cents={t.balanceAfter} />
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )
      ) : data.isPending ? (
        <Spinner />
      ) : data.error ? (
        <ErrorBlock error={data.error} onRetry={() => data.refetch()} />
      ) : (
        <>
          <section className="mt-2 grid grid-cols-2 gap-px bg-neutral-200 text-center">
            {[
              ["待结算", data.data.summary.pending],
              ["可结算", data.data.summary.settleable],
              ["已入账", data.data.summary.settled],
              ["已冲抵", data.data.summary.reversed],
            ].map(([label, v]) => (
              <div key={label as string} className="bg-white py-3">
                <p className="text-xs text-ink-500">{label}</p>
                <p className="mt-1">
                  <Price cents={v as number} />
                </p>
              </div>
            ))}
          </section>

          {/* ⚠️ 说清楚为什么有负数，不解释的话商家会以为系统扣错了 */}
          <p className="px-4 py-2 text-xs text-ink-500">
            负数是<strong>你支出的部分</strong>：自己发的优惠券、被上级抽的成、
            退款时扣回的钱。
          </p>

          {data.data.items.length === 0 ? (
            <Empty title="还没有收益记录" hint="有客户下单后，每一笔分账都会列在这里" />
          ) : (
            <div className="mt-1 bg-white">
              {data.data.items.map((it) => (
                <div
                  key={it.id}
                  className="flex items-start gap-3 border-b border-neutral-100 px-4 py-3 last:border-0"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm">
                      {TYPE_LABEL[it.type] ?? it.type}
                      <span className="ml-1.5 rounded bg-neutral-100 px-1.5 py-0.5 text-[11px] text-ink-500">
                        {STATUS_LABEL[it.status] ?? it.status}
                      </span>
                    </p>
                    {it.note && <p className="mt-0.5 text-xs text-ink-500">{it.note}</p>}
                    <p className="mt-0.5 text-xs text-ink-500">
                      订单 #{it.orderId} · {new Date(it.createdAt).toLocaleDateString("zh-CN")}
                      {/* 待结算的钱要等售后期，标出解冻时间省得他反复问 */}
                      {it.status === "pending" && it.settleableAt && (
                        <span className="ml-1">
                          · {new Date(it.settleableAt).toLocaleDateString("zh-CN")} 可结
                        </span>
                      )}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 text-sm ${
                      it.amount < 0 ? "text-red-600" : "text-brand-600"
                    }`}
                  >
                    {it.amount < 0 ? "−" : "+"}
                    <Price cents={Math.abs(it.amount)} />
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </SubPage>
  );
}

/** 手机版 / 网页版是两套页面，按视口挑一套（URL 与数据 hooks 两边共用）。 */
function EarningsRoute() {
  return useIsDesktop() ? <DesktopEarnings /> : <Earnings />;
}

export const earningsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/earnings",
  component: EarningsRoute,
  validateSearch: (s: Record<string, unknown>): { orgId?: number } => ({
    orgId: s.orgId ? Number(s.orgId) : undefined,
  }),
});
